
import { ReportTemplate } from '@/types/reportTypes';
import { handleError, ErrorSeverity } from '@/utils/errorHandler';

export interface TemplateFilterState {
  selectedCategories: string[];
  searchTerm: string;
}

export interface TemplateFilterLogicResult {
  filterTemplates: (templates: ReportTemplate[], categories?: string[], searchTerm?: string) => ReportTemplate[];
  handleCategoryChange: (
    category: string,
    selectedCategories: string[],
    allCategoriesOption?: string
  ) => string[];
  error: Error | null;
}

export function useTemplateFilterLogic(): TemplateFilterLogicResult {
  let logicError: Error | null = null;

  const handleCategoryChange = (
    category: string,
    selectedCategories: string[],
    allCategoriesOption: string = "All Categories"
  ): string[] => {
    try {
      if (!category) {
        throw new Error("Category cannot be empty");
      }
      if (category === allCategoriesOption) {
        return [allCategoriesOption];
      } else {
        const newCategories = selectedCategories.includes(allCategoriesOption)
          ? [category]
          : selectedCategories.includes(category)
            ? selectedCategories.filter(c => c !== category)
            : [...selectedCategories, category];
        return newCategories.length ? newCategories : [allCategoriesOption];
      }
    } catch (error) {
      handleError(
        error,
        "Error changing template category filter",
        ErrorSeverity.WARNING,
        {
          operation: "handleCategoryChange",
          additionalInfo: { category, currentSelection: selectedCategories }
        },
        false
      );
      logicError = error instanceof Error ? error : new Error(String(error));
      return selectedCategories;
    }
  };

  const filterTemplates = (
    templates: ReportTemplate[],
    categories?: string[],
    searchTerm?: string
  ): ReportTemplate[] => {
    try {
      if (!templates || !Array.isArray(templates)) {
        throw new Error("Invalid templates array provided to filter");
      }

      let filtered = templates;
      const selectedCategories = categories ?? [];
      const term = searchTerm ?? "";

      // Apply category filter
      if (selectedCategories.length > 0 && !selectedCategories.includes("All Categories")) {
        filtered = filtered.filter((template) => {
          if (!template.category) return false;
          return selectedCategories.includes(template.category);
        });
      }

      // Apply search filter
      if (term.trim()) {
        const pattern = term.trim().toLowerCase();
        filtered = filtered.filter((template) => {
          const nameMatch = typeof template.name === 'string' &&
            template.name.toLowerCase().includes(pattern);
          const descMatch = typeof template.description === 'string' &&
            template.description.toLowerCase().includes(pattern);
          return nameMatch || descMatch;
        });
      }

      return filtered;
    } catch (error) {
      handleError(
        error,
        "Error filtering templates",
        ErrorSeverity.WARNING,
        {
          operation: "filterTemplates",
          additionalInfo: {
            categoriesCount: categories?.length || 0,
            hasSearchTerm: !!searchTerm?.trim(),
            templatesCount: templates?.length || 0
          }
        },
        false
      );
      logicError = error instanceof Error ? error : new Error(String(error));
      return [];
    }
  };

  return {
    filterTemplates,
    handleCategoryChange,
    error: logicError
  };
}
